import { FC, useCallback, useState } from 'react';

import { useResetRecoilState, useSetRecoilState } from 'recoil';

import { SearchIcon } from '@heroicons/react/outline';
import { GIFT_STORE_SELF_ORDER_DAYS } from '@service/giftStore/hooks/useGiftStoreAccess';
import { giftDeliveryAddressAtom } from '@service/giftStore/state/giftDeliveryAddress.atom';
import { giftThanksPrintAtom } from '@service/giftStore/state/giftThanksPrint.atom';
import { parseThanksSender } from '@service/giftStore/utils/thanksPrintMessage';
import { Bugo } from '@shared/api/bugo/bugo.interface';
import { Fevent } from '@shared/api/fevent/fevent.interface';
import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { Member } from '@shared/api/member/member.interface';
import { RoomInfo } from '@shared/api/roomInfo/roomInfo.interface';
import {
  FeventOrderHistory,
  fetchFeventOrderHistory,
  isSameMemberOrder,
  prefetchFeventOrderCounts,
} from '@shared/components/GiftFeventOrderHistory';
import { Loading } from '@shared/components/Loading';
import { api_ } from '@shared/plugIn/axios';
import { useAuth } from '@shared/state/hooks/useAuth';
import { formatFeventDateString } from '@shared/utils/formatDate';
import {
  isSameMemberAsLastAgentOrder,
  startGiftAgentOrder,
} from '@shared/utils/giftAgentOrder.util';
import { getReverseType } from '@shared/utils/memberUtils';
import { useNavigate } from 'react-router-dom';

import { useSessionPersistedState } from '../hooks/useSessionPersistedState';

/**
 * 상주 검색 섹션 (Admin·GiftAgent) — 전화주문 관리 페이지의 "주문 생성" 탭.
 *
 * 이름/전화번호로 상주(member)를 찾아
 * - 답례글 화면(/member/:id/courtesy)으로 이동하거나
 * - "대리주문"으로 해당 상주의 답례품 스토어에 진입한다.
 *   대리주문은 발인+30일 온라인 마감 이후의 전화 주문 대응이 주 용도로,
 *   결제 시 상주 명의 게스트가 만들어지고 가상계좌 입금안내가 상주 번호로 발송된다.
 *   (스토어 쪽 배선은 giftAgentOrder.util 참고)
 *
 * 검색 경로는 role별로 다르다 (docs/gift-member-search-spec.md):
 * - Admin: 기존 /admin/member 제네릭 (지금도 동작)
 * - GiftAgent: 신규 /gift-agent/member/search — Admin 제네릭은 권한 부족에
 *   401을 응답해 axios 강제 로그아웃으로 콘솔에서 튕기므로 호출하면 안 된다.
 *   미배포 서버에선 라우트 부재로 404(가드 없음 → 튕김 없음) → 배포 대기 안내.
 */

const SEARCH_LIMIT = 50;

type SearchFevent = Fevent<string, FuneralHomeInfo, RoomInfo>;
type SearchMember = Member<SearchFevent, Bugo>;

const escapeRegex = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

//* 저장형식(01012345678 / 010-1234-5678 / +82 10-...)과 무관하게 매칭되는 패턴.
//* 백엔드 phone-normalize.ts / AdminCourtesyManagePage와 같은 규칙.
const PHONE_SEARCH_MIN_DIGITS = 4;

const buildPhoneRegexPattern = (input: string): string | null => {
  const digits = input.replace(/\D/g, '');
  if (digits.length < PHONE_SEARCH_MIN_DIGITS) return null;

  const candidates = new Set<string>([digits]);
  if (digits.startsWith('0')) candidates.add('82' + digits.slice(1));
  if (digits.startsWith('82') && digits.length >= 11)
    candidates.add('0' + digits.slice(2));

  return Array.from(candidates)
    .map((candidate) => candidate.split('').join('\\D*'))
    .join('|');
};

//* 정규식 백슬래시가 그대로 실려나가지 않도록 쿼리를 직접 인코딩한다.
//* select 같은 문자열 파라미터는 JSON 따옴표 없이 그대로 실어야 한다.
const adminQuery = (query: {
  filter?: unknown;
  populate?: unknown;
  options?: unknown;
  select?: string;
}) =>
  Object.entries(query)
    .filter(([, value]) => value !== undefined)
    .map(
      ([key, value]) =>
        `${key}=${encodeURIComponent(
          typeof value === 'string' ? value : JSON.stringify(value),
        )}`,
    )
    .join('&');

const searchMemberList = async (
  name: string,
  phone: string,
): Promise<SearchMember[]> => {
  const conditions: unknown[] = [];
  if (name) conditions.push({ fullName: { $regex: escapeRegex(name) } });
  const phonePattern = phone ? buildPhoneRegexPattern(phone) : null;
  if (phonePattern) conditions.push({ phoneNumber: { $regex: phonePattern } });

  const filter = conditions.length === 1 ? conditions[0] : { $and: conditions };
  const { data } = await api_.get<SearchMember[]>(
    `/admin/member/?${adminQuery({
      filter,
      populate: [
        { path: 'bugo' },
        {
          path: 'fevent',
          populate: [
            { path: 'funeralHomeInfo' },
            { path: 'roomInfo' },
            { path: 'bugoBrand' },
          ],
        },
      ],
      options: { sort: { createdAt: -1 }, limit: SEARCH_LIMIT },
    })}`,
  );
  return data;
};

//* GiftAgent용 전용 검색 — 응답은 admin 제네릭과 같은 populate된 member 배열 계약
const searchMemberListAsGiftAgent = async (
  name: string,
  phone: string,
): Promise<SearchMember[]> => {
  const params = new URLSearchParams();
  if (name) params.set('name', name);
  if (phone) params.set('phoneNumber', phone);
  const { data } = await api_.get<SearchMember[]>(
    `/gift-agent/member/search?${params.toString()}`,
  );
  return data;
};

const getAgencyName = (fevent?: SearchFevent): string | undefined => {
  const bugoBrand = fevent?.bugoBrand as unknown as
    | { bugoAgency?: { nickName?: string; corpName?: string } }
    | undefined;
  return bugoBrand?.bugoAgency?.nickName ?? bugoBrand?.bugoAgency?.corpName;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const startOfDay = (date: Date): number => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

//* 발인 후 경과일 (발인일 없음/이상값이면 null) — 날짜(자정) 기준
const daysSinceCoffinOut = (fevent?: SearchFevent): number | null => {
  const rawCoffinOut = fevent?.deceasedInfo?.coffinOut?.date;
  if (!rawCoffinOut) return null;
  const coffinOutDate = new Date(rawCoffinOut);
  if (Number.isNaN(coffinOutDate.getTime())) return null;
  return Math.floor((startOfDay(new Date()) - startOfDay(coffinOutDate)) / MS_PER_DAY);
};

//* 셀프 온라인 주문 마감 여부 — useGiftStoreAccess와 같은 규칙
const isSelfOrderClosed = (fevent?: SearchFevent): boolean => {
  const days = daysSinceCoffinOut(fevent);
  return days !== null && days > GIFT_STORE_SELF_ORDER_DAYS;
};

//* 발인 후 이 일수를 넘긴 상가는 검색 결과에서 제외 — 데이터 분석상 답례품
//* 구매가 이 기간 안에서만 일어나 그 밖은 응대 대상이 아님. (발인 미정은 유지)
const PHONE_ORDER_SEARCH_MAX_DAYS = 45;

const isWithinSalesWindow = (member: SearchMember): boolean => {
  const days = daysSinceCoffinOut(member.fevent as SearchFevent | undefined);
  return days === null || days <= PHONE_ORDER_SEARCH_MAX_DAYS;
};

//* 지난 주문 추천용 — 같은 부고의 최근 주문에서 감사문구·배송지를 가져온다.
//* 전용 라우트 우선(Admin·GiftAgent 공용), 미배포 404면 Admin만 제네릭 폴백.
//* 실패해도 주문 흐름엔 지장 없음.
interface PrevOrderSuggestion {
  thanksPrint?: { templateId?: string; message?: string; senderName?: string };
  bulkDeliveryAddress?: {
    address?: string;
    postalCode?: string;
    addressDetail?: string;
  };
}

//* 발인+45일 판매창 + 여유를 덮는 조회 기간 (admin 목록 기본 7일을 넘어서도록 명시)
const SUGGESTION_LOOKBACK_DAYS = 120;

const fetchPrevOrderSuggestion = async (
  bugoId: string,
  canUseAdminFallback: boolean,
): Promise<PrevOrderSuggestion[]> => {
  try {
    const { data } = await api_.get<PrevOrderSuggestion[]>(
      `/gift-agent/gift-order/suggestion?bugo=${bugoId}`,
    );
    return data;
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status !== 404 || !canUseAdminFallback) throw err;
    //* 구서버 폴백 — from 명시 필수(admin 목록 기본 조회기간 7일 함정)
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - SUGGESTION_LOOKBACK_DAYS);
    const from = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(
      2,
      '0',
    )}-${String(fromDate.getDate()).padStart(2, '0')}`;
    const { data } = await api_.get<PrevOrderSuggestion[]>(
      `/admin/gift-order?${adminQuery({
        filter: { bugo: bugoId, docStatus: { $ne: 'Deleted' } },
        options: { sort: { createdAt: -1 }, limit: 10 },
        select: 'thanksPrint bulkDeliveryAddress createdAt',
      })}&from=${from}`,
    );
    return data;
  }
};

/**
 * 프리필용 지난 주문 — **이름+전화번호가 완전히 일치하는 본인 주문만** 쓴다.
 *
 * 같은 상가(fevent)엔 다른 상주(가족)의 주문도 섞여 있어, 상가 이력에서 본인 것만 골라낸다.
 * (이력은 검색 직후 배지용으로 이미 조회돼 캐시에 있어 추가 지연이 없다)
 * 본인 주문이 없으면 프리필하지 않는다 — 남의 감사글·주소가 미리 채워지는 쪽이 더 나쁘다.
 * 이력 조회가 안 되는 서버에서만 부고 단위 추천(그 상주 본인 링크)으로 폴백한다.
 */
const fetchMemberPrevOrders = async (
  bugoId: string,
  feventId: string | undefined,
  memberName: string,
  memberPhone: string,
  canUseAdminFallback: boolean,
): Promise<PrevOrderSuggestion[]> => {
  if (feventId) {
    try {
      const history = await fetchFeventOrderHistory(feventId, canUseAdminFallback);
      return history.filter((order) =>
        isSameMemberOrder(order, memberName, memberPhone),
      );
    } catch (err) {
      console.error(err);
    }
  }
  return fetchPrevOrderSuggestion(bugoId, canUseAdminFallback);
};


const MemberRow: FC<{
  member: SearchMember;
  isHistoryOpen: boolean;
  onToggleHistory: () => void;
  orderCount?: number;
}> = function MemberRow({ member, isHistoryOpen, onToggleHistory, orderCount }) {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const fevent = member.fevent as SearchFevent | undefined;
  const funeralHomeName =
    fevent?.funeralHomeInfoEmbed?.name ?? fevent?.funeralHomeInfo?.name;
  const closed = isSelfOrderClosed(fevent);
  const bugoId = member.bugo?._id;

  const resetThanksPrint = useResetRecoilState(giftThanksPrintAtom);
  const resetDeliveryAddress = useResetRecoilState(giftDeliveryAddressAtom);
  const setThanksPrint = useSetRecoilState(giftThanksPrintAtom);
  const setDeliveryAddress = useSetRecoilState(giftDeliveryAddressAtom);

  const handleAgentOrder = useCallback(async () => {
    if (!bugoId) return;
    const confirmed = window.confirm(
      `${member.fullName}님 명의로 대리주문을 시작합니다.\n\n` +
        `· 상품을 골라 결제하면 상주 번호(${member.phoneNumber})로 입금안내 문자가 발송됩니다.\n` +
        `· 주문을 마친 뒤 화면의 "종료" 버튼으로 이 화면에 돌아올 수 있습니다.`,
    );
    if (!confirmed) return;
    // 다른 상주로 시작하면 이전 주문의 입력 잔재(감사메시지·배송지)를 비운다 —
    // 이전 상주의 감사카드/주소가 그대로 딸려 들어가는 사고 방지.
    // 같은 상주 재주문이면 유지(재입력 수고 절약).
    if (!isSameMemberAsLastAgentOrder(member._id)) {
      resetThanksPrint();
      resetDeliveryAddress();
      // 이 상주가 전에 주문한 적이 있으면 그 주문의 감사문구·배송지를 미리 채워 추천.
      // (이름+번호 완전일치 본인 주문만. 같은 상가 다른 상주의 주문은 자동으로
      //  들어가지 않고, 스토어 옆 이력 패널에서 직원이 눌러야만 들어간다)
      // 보존값에 ownerBugoId 도장을 함께 남겨야 다른 상주 주문서로 새어나가지 않는다.
      // 체크아웃의 기존 복원 로직(감사문구 초기화·배송지 자동 채움/칩)이 그대로 소비한다.
      // GiftAgent 전용 라우트가 미배포(404)면 추천 없이 진행.
      {
        try {
          const prevOrders = await fetchMemberPrevOrders(
            bugoId,
            (member.fevent as SearchFevent | undefined)?._id,
            member.fullName,
            member.phoneNumber,
            isAdmin(),
          );
          const prevThanks = prevOrders.find((o) => o.thanksPrint?.message)?.thanksPrint;
          if (prevThanks?.message) {
            const parsed = parseThanksSender(prevThanks.senderName, undefined);
            setThanksPrint({
              message: prevThanks.message,
              senderName: parsed.name,
              senderSuffix: parsed.suffix,
              templateId: prevThanks.templateId,
              ownerBugoId: bugoId,
            });
          }
          const prevAddress = prevOrders.find(
            (o) => o.bulkDeliveryAddress?.address && o.bulkDeliveryAddress?.postalCode,
          )?.bulkDeliveryAddress;
          if (prevAddress?.address && prevAddress.postalCode) {
            setDeliveryAddress({
              address: prevAddress.address,
              postalCode: prevAddress.postalCode,
              addressDetail: prevAddress.addressDetail,
              ownerBugoId: bugoId,
            });
          }
        } catch (err) {
          console.error(err);
        }
      }
    }
    const agentFevent = member.fevent as SearchFevent | undefined;
    startGiftAgentOrder({
      bugoId,
      memberId: member._id,
      memberName: member.fullName,
      memberPhone: member.phoneNumber,
      feventId: agentFevent?._id,
      deceasedName: agentFevent?.deceasedInfo?.name,
      relationType: member.relationType,
      //* 부친상·시모상처럼 고인 성별까지 반영한 표기 (부고 문구와 같은 규칙)
      reverseType: agentFevent?.deceasedInfo?.sex
        ? getReverseType(member.relationType ?? '', agentFevent.deceasedInfo.sex)
        : undefined,
    });
    navigate(`/gift-store?bugoId=${bugoId}`);
  }, [
    bugoId,
    member,
    navigate,
    isAdmin,
    resetThanksPrint,
    resetDeliveryAddress,
    setThanksPrint,
    setDeliveryAddress,
  ]);

  const feventId = (member.fevent as SearchFevent | undefined)?._id;

  return (
    <>
    <tr className="hover:bg-gray-50">
      <td className="whitespace-nowrap px-4 py-3 text-sm">
        <span className="font-medium text-gray-900">{member.fullName}</span>
        {member.relationType && (
          <span className="ml-1 text-xs text-gray-500">({member.relationType})</span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
        {member.phoneNumber || '-'}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
        {fevent?.deceasedInfo?.name ?? '-'}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
        {fevent?.deceasedInfo?.coffinOut?.date
          ? formatFeventDateString(fevent.deceasedInfo.coffinOut, false)
          : '-'}
        {closed && (
          <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
            온라인주문 마감
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
        {getAgencyName(fevent) ?? '-'}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
        {funeralHomeName ?? '-'}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
        <div className="flex justify-end gap-2">
          {feventId && (
            <button
              onClick={onToggleHistory}
              className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                orderCount
                  ? 'border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {isHistoryOpen ? '주문내역 닫기' : '주문내역'}
              {orderCount ? `(${orderCount})` : ''}
            </button>
          )}
          <a
            href={`/member/${member._id}/courtesy`}
            target="_blank"
            rel="noreferrer"
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            답례글 화면
          </a>
          <button
            onClick={handleAgentOrder}
            disabled={!bugoId}
            title={bugoId ? undefined : '부고가 없는 상주는 대리주문을 시작할 수 없습니다'}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            대리주문
          </button>
        </div>
      </td>
    </tr>
    {isHistoryOpen && feventId && (
      <tr>
        <td colSpan={7} className="bg-gray-50 px-4 py-3">
          {/* 이 상주 본인(이름+번호 일치) 주문이 위로, 다른 상주 주문은 구분 표기 */}
          <FeventOrderHistory
            feventId={feventId}
            layout="grid"
            memberName={member.fullName}
            memberPhone={member.phoneNumber}
          />
        </td>
      </tr>
    )}
    </>
  );
};

export const GiftMemberSearchSection: FC = function GiftMemberSearchSection() {
  const { isAdmin } = useAuth();

  // 검색 입력 상태 — 스토어를 다녀온 뒤에도 유지되도록 sessionStorage에 저장
  const [name, setName] = useSessionPersistedState('giftMemberSearch.name', '');
  const [phone, setPhone] = useSessionPersistedState('giftMemberSearch.phone', '');

  const [memberList, setMemberList] = useState<SearchMember[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  //* 주문내역을 펼친 행 (한 번에 하나만)
  const [openHistoryMemberId, setOpenHistoryMemberId] = useState<string | null>(null);
  //* 상가별 주문 건수 — 펼치기 전에도 "주문내역(4)"로 보이도록 검색 직후 선조회
  const [orderCounts, setOrderCounts] = useState<Map<string, number>>(new Map());

  const handleSearch = useCallback(async () => {
    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedName && !trimmedPhone) {
      alert('상주 이름 또는 전화번호를 입력해주세요.');
      return;
    }
    if (trimmedPhone && !buildPhoneRegexPattern(trimmedPhone)) {
      alert(`전화번호는 숫자 ${PHONE_SEARCH_MIN_DIGITS}자리 이상 입력해주세요.`);
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    try {
      const result = await (isAdmin()
        ? searchMemberList(trimmedName, trimmedPhone)
        : searchMemberListAsGiftAgent(trimmedName, trimmedPhone));
      const visible = result.filter(isWithinSalesWindow);
      setMemberList(visible);
      setOrderCounts(new Map());
      //* 건수 배지는 목록을 먼저 띄운 뒤 채운다 (검색 결과가 늦어지지 않도록)
      const feventIds = Array.from(
        new Set(
          visible
            .map((member) => (member.fevent as SearchFevent | undefined)?._id)
            .filter((id): id is string => Boolean(id)),
        ),
      );
      if (feventIds.length > 0) {
        prefetchFeventOrderCounts(feventIds, isAdmin()).then(setOrderCounts);
      }
    } catch (err) {
      console.error(err);
      setMemberList(null);
      const status = (err as { response?: { status?: number } })?.response?.status;
      setErrorMessage(
        !isAdmin() && status === 404
          ? '상주 검색 기능이 아직 서버에 준비되지 않았습니다. 서버 업데이트 후 사용할 수 있습니다.'
          : status === 401 || status === 403
          ? '검색 권한이 없습니다. 관리자에게 문의해주세요.'
          : '검색에 실패했습니다. 잠시 후 다시 시도해주세요.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [name, phone, isAdmin]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') handleSearch();
    },
    [handleSearch],
  );

  const handleReset = useCallback(() => {
    setName('');
    setPhone('');
    setMemberList(null);
    setErrorMessage(null);
  }, [setName, setPhone]);

  return (
    <div>
      {/* 검색 폼 */}
      <div className="mb-6 rounded-lg bg-white p-4 shadow">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">상주 이름</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="홍길동"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">전화번호</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="010-1234-5678 (뒷자리 4개 이상)"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-indigo-300"
            >
              <SearchIcon className="h-4 w-4" />
              검색
            </button>
            <button
              onClick={handleReset}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              초기화
            </button>
          </div>
        </div>
      </div>

      {/* 결과 */}
      {isLoading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : errorMessage ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-red-600 shadow">
          {errorMessage}
        </div>
      ) : memberList ? (
        memberList.length === 0 ? (
          <div className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 shadow">
            검색 결과가 없습니다. (발인 45일이 지난 상가는 나오지 않아요)
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg bg-white shadow">
            <div className="border-b border-gray-200 px-4 py-2 text-xs text-gray-500">
              {memberList.length}건
              {memberList.length >= SEARCH_LIMIT &&
                ` (최근 등록순 최대 ${SEARCH_LIMIT}건만 표시 — 검색어를 좁혀주세요)`}
            </div>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['상주', '전화번호', '고인', '발인', '상조', '장례식장', ''].map(
                    (header, idx) => (
                      <th
                        key={idx}
                        className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        {header}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {memberList.map((member) => (
                  <MemberRow
                    key={member._id}
                    member={member}
                    isHistoryOpen={openHistoryMemberId === member._id}
                    onToggleHistory={() =>
                      setOpenHistoryMemberId((curr) =>
                        curr === member._id ? null : member._id,
                      )
                    }
                    orderCount={orderCounts.get(
                      (member.fevent as SearchFevent | undefined)?._id ?? '',
                    )}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </div>
  );
};
